import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "../../../store";
import type AddTagsDialogNewProps from "./AddTagsDialogNewProps";
import { ComboBox, DefaultButton, DialogFooter, DialogType, type IComboBox, type IComboBoxOption, PrimaryButton } from "@fluentui/react";
import { type FormEvent, useState } from "react";
import type ApiResponse from "../../../services/api/types/ApiResponse";
import { type Tag } from "../../../types/Tag";
import useTagNewHook from "../../../hooks/useTagNewHook";
import { addTags } from "../../../pages/constructionNew/constructionNewSlice";
import IsaacDialog from "../../isaacDialog/IsaacDialog";

const AddTagsDialogNew: React.FunctionComponent<AddTagsDialogNewProps> = ({
    isHidden,
    handleDismiss
}) => {
    const {tags} = useSelector((state: RootState) => state.constructionsNew);
    const {data} = useSelector((state: RootState) => state.constructionNew);

    const [currentTags, setCurrentTags] = useState<IComboBoxOption[]>(tags);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [result, setResult] = useState<ApiResponse<Tag[]>>();
    const {addTagsToConstruction} = useTagNewHook();
    const dispatch = useDispatch();

    const handleChange = (
        event: FormEvent<IComboBox>,
        option?: IComboBoxOption | undefined,
        index?: number | undefined,
        value?: string | undefined,
    ): void => {
        if (option !== undefined) {
          setCurrentTags((preState: IComboBoxOption[]) => [
            ...preState.map(el =>
              el.key === option.key ? {...el, selected: option.selected} : el,
            ),
          ]);
    
          setSelectedTags(prevState =>
            option.selected === true
              ? [...prevState?.filter(o => o !== option.text), option.text]
              : [...prevState?.filter(o => o !== option.text)],
          );
          return;
        }
        if (value !== undefined) {
          setCurrentTags((preState: IComboBoxOption[]) => [
            ...preState,
            {key: value, text: value, selected: true},
          ]);
          setSelectedTags(prevState => [...prevState, value]);
        }
    };

    const handleAddTags = async (): Promise<void> => {
        setCurrentTags((preState: IComboBoxOption[]) => [
            ...preState.map(el => ({...el, selected: false})),
        ]);

        setIsLoading(true);

        if (data) {
            const response = await addTagsToConstruction(data.id, selectedTags.map(t => ({Value: t})));
            setResult(response);
            dispatch(addTags(selectedTags.map(t => ({value: t}))));
        }
        setSelectedTags([]);
        handleDismiss();
        setIsLoading(false);
    };

    const handleDismissAndClearState = (): void => {
        setCurrentTags((preState: IComboBoxOption[]) => [
          ...preState.map(el => ({...el, selected: false})),
        ]);
    
        setSelectedTags([]);
        handleDismiss();
    };

    return (
        <IsaacDialog
          isHidden={isHidden}
          dialogContentProps={{
            type: DialogType.normal,
            title: "Add tag to the construction",
          }}
          modalProps={{
            titleAriaId: "tag",
            subtitleAriaId: "mySubTextId",
            isBlocking: true,
            containerClassName: "ms-dialogMainOverride ",
          }}
          isLoading={isLoading}
          errorMessage={result?.errors}
          successMessage="Calculation created successfully"
          handleDialogDismiss={handleDismiss}>
          <>
            <ComboBox
              label="Add a new tag or select from existing tags."
              allowFreeform={true}
              multiSelect
              selectedKey={selectedTags}
              options={currentTags}
              onChange={handleChange}
            />
            <DialogFooter>
              <PrimaryButton
                onClick={handleAddTags}
                text="Add"
                disabled={selectedTags !== undefined && selectedTags.length <= 0}
              />
              <DefaultButton onClick={handleDismissAndClearState} text="Cancel" />
            </DialogFooter>
          </>
        </IsaacDialog>
    );
};

export default AddTagsDialogNew;